/**
 * src/services/adminService.js
 * 
 * Centralized admin service layer.
 * Handles all admin CRUD operations against Firestore.
 * Works on Firebase Spark plan 
 * 
 * Collections managed:
 * - admin_notifications: Admin-sent announcements & targeted messages
 * - admin_feedback: User feedback submissions
 * - support_tickets: Support ticket management
 * - users: User management (role assignment, banning)
 * - products: Content moderation
 * - admin_activity_log: Audit trail for admin actions
 */

import { db } from '../lib/firebase';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  documentId,
  limit,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  startAfter,
  getCountFromServer,
  increment,
  runTransaction,
} from '../lib/firestoreClient';
import { notifyPointsEarned } from './notificationsService';
import { awardImpactPointsInTransaction, IMPACT_ACTIONS } from './impactPointsService';
import { errorDev } from '../utils/logger.js';

/**
 * Retry transient admin operations for unstable network conditions.
 */
export const withAdminRetry = async (operation, retries = 2, delayMs = 500) => {
  let lastError;
  const transientMarkers = [
    'aborted',
    'cancelled',
    'deadline-exceeded',
    'internal assertion failed',
    'network',
    'unavailable',
  ];
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await operation();
    } catch (err) {
      lastError = err;
      const message = String(err?.message || '');
      const code = String(err?.code || err?.details?.firestoreCode || '').toLowerCase();
      const isQuotaError = err?.status === 429
        || /quota|rate limit/i.test(message)
        || ['resource-exhausted', 'quota-exceeded'].some((marker) => code.includes(marker));
      const isTransient = err?.retryable === true
        || transientMarkers.some((marker) => message.toLowerCase().includes(marker) || code.includes(marker));
      if (attempt >= retries || isQuotaError || !isTransient) break;
      await new Promise((resolve) => setTimeout(resolve, delayMs * (attempt + 1)));
    }
  }
  throw lastError;
};

// ─── Constants ──────────────────────────────────────────────────
const COLLECTIONS = {
  USERS: 'users',
  PRODUCTS: 'products',
  NOTIFICATIONS: 'notifications',
  ADMIN_NOTIFICATIONS: 'admin_notifications',
  ADMIN_FEEDBACK: 'admin_feedback',
  SUPPORT_TICKETS: 'support_tickets',
  ACTIVITY_LOG: 'admin_activity_log',
  RATINGS: 'ratings',
  OFFERS: 'offers',
  REPORTS: 'reports',
};

const ENTITY_LABEL_FALLBACK = {
  profile: 'profile',
  product: 'product',
};

const STATUS_LABELS = {
  open: 'Open',
  under_review: 'Under Review',
  resolved: 'Resolved',
  rejected: 'Rejected',
};

function statusLabel(status) {
  if (!status) return 'Open';
  return STATUS_LABELS[String(status)] || String(status).replace(/_/g, ' ');
}

function normalizeName(value) {
  return String(value || '').trim();
}

function pickEntityLabel(entityType, details = {}) {
  const candidates = [
    details.entityLabel,
    details.entityTitle,
    details.productTitle,
    details.profileName,
    details.profileUsername,
    details.username,
  ];

  for (const item of candidates) {
    const text = normalizeName(item);
    if (text) return text;
  }

  const idFallback = normalizeName(details.entityId || details.reportId || details.productId || details.userId);
  if (idFallback) {
    const kind = ENTITY_LABEL_FALLBACK[entityType] || 'item';
    return `${kind} #${idFallback.slice(0, 8)}`;
  }

  return ENTITY_LABEL_FALLBACK[entityType] || 'item';
}

function toActivityDescription(action, details = {}, fallback = '') {
  const existing = normalizeName(fallback);
  if (existing) return existing;

  switch (action) {
    case 'UPDATE_USER_ROLE': {
      const target = normalizeName(details.userName || details.userEmail || details.userId) || 'a user';
      const role = normalizeName(details.newRole) || 'user';
      return `updated ${target} role to ${role}`;
    }
    case 'BAN_USER': {
      const target = normalizeName(details.userName || details.userEmail || details.userId) || 'a user';
      return `banned ${target}`;
    }
    case 'UNBAN_USER': {
      const target = normalizeName(details.userName || details.userEmail || details.userId) || 'a user';
      return `unbanned ${target}`;
    }
    case 'UPDATE_REPORT_STATUS': {
      const entityType = String(details.entityType || 'report').toLowerCase();
      const entityLabel = pickEntityLabel(entityType, details);
      const nextStatus = statusLabel(details.status);
      return `moved ${entityLabel} report to ${nextStatus}`;
    }
    case 'MODERATE_PRODUCT': {
      const actionLabel = normalizeName(details.action) || 'updated';
      const entityLabel = pickEntityLabel('product', details);
      if (actionLabel === 'remove') return `removed ${entityLabel}`;
      if (actionLabel === 'hide') return `hid ${entityLabel}`;
      return `approved ${entityLabel}`;
    }
    case 'DELETE_REPORT': {
      const entityType = String(details.entityType || 'report').toLowerCase();
      const entityLabel = pickEntityLabel(entityType, details);
      return `deleted report for ${entityLabel}`;
    }
    case 'BULK_DELETE_REPORTS':
      return `deleted ${Number(details.count || 0)} reports`;
    default:
      return String(action || 'admin action').replace(/_/g, ' ').toLowerCase();
  }
}

function decorateActivityEntry(entry) {
  const details = entry?.details && typeof entry.details === 'object' ? entry.details : {};
  const humanDescription = toActivityDescription(entry?.action, details, entry?.description);

  let detailLine = '';
  if (details.reason) {
    detailLine = String(details.reason);
  } else if (details.status) {
    detailLine = `Status: ${statusLabel(details.status)}`;
  }

  return {
    ...entry,
    humanDescription,
    detailLine,
  };
}

async function getCurrentAdminRole(adminUser) {
  if (!adminUser?.uid) return null;

  const snap = await getDoc(doc(db, COLLECTIONS.USERS, adminUser.uid));
  if (!snap.exists()) return null;
  return snap.data()?.role || null;
}

async function ensureSuperAdmin(adminUser) {
  const role = await getCurrentAdminRole(adminUser);
  if (role !== 'superadmin') {
    throw new Error('Only super admins can delete reports');
  }
}

const reportLabelCache = new Map();
const userLabelCache = new Map();

function cacheKey(prefix, id) {
  return `${prefix}:${id}`;
}

function entityCollectionFromType(entityType) {
  if (entityType === 'profile') return COLLECTIONS.USERS;
  if (entityType === 'product') return COLLECTIONS.PRODUCTS;
  return null;
}

function firstNonEmpty(...values) {
  for (const value of values) {
    const text = normalizeName(value);
    if (text) return text;
  }
  return '';
}

async function resolveUserLabel(userId, fallback = '') {
  if (!userId) return firstNonEmpty(fallback, 'Unknown user');

  const key = cacheKey('user', userId);
  if (userLabelCache.has(key)) return userLabelCache.get(key);

  try {
    const snap = await getDoc(doc(db, COLLECTIONS.USERS, userId));
    if (snap.exists()) {
      const data = snap.data() || {};
      const label = firstNonEmpty(data.displayName, data.name, data.username, data.email, fallback, `User #${String(userId).slice(0, 8)}`);
      userLabelCache.set(key, label);
      return label;
    }
  } catch (err) {
    // Keep fallback path.
  errorDev("Operation failed:", err);
  }

  const fallbackLabel = firstNonEmpty(fallback, `User #${String(userId).slice(0, 8)}`);
  userLabelCache.set(key, fallbackLabel);
  return fallbackLabel;
}

async function resolveEntityLabel(entityType, entityId, fallback = '') {
  if (!entityType || !entityId) return firstNonEmpty(fallback, ENTITY_LABEL_FALLBACK[entityType] || 'item');

  const key = cacheKey(entityType, entityId);
  if (reportLabelCache.has(key)) return reportLabelCache.get(key);

  const collectionName = entityCollectionFromType(entityType);
  if (!collectionName) {
    const label = firstNonEmpty(fallback, `${ENTITY_LABEL_FALLBACK[entityType] || 'item'} #${String(entityId).slice(0, 8)}`);
    reportLabelCache.set(key, label);
    return label;
  }

  try {
    const snap = await getDoc(doc(db, collectionName, entityId));
    if (snap.exists()) {
      const data = snap.data() || {};
      const label = firstNonEmpty(
        fallback,
        data.title,
        data.name,
        data.username,
        data.displayName,
        data.productName,
        `${ENTITY_LABEL_FALLBACK[entityType] || 'item'} #${String(entityId).slice(0, 8)}`
      );
      reportLabelCache.set(key, label);
      return label;
    }
  } catch (err) {
    // Keep fallback path.
  errorDev("Operation failed:", err);
  }

  const fallbackLabel = firstNonEmpty(fallback, `${ENTITY_LABEL_FALLBACK[entityType] || 'item'} #${String(entityId).slice(0, 8)}`);
  reportLabelCache.set(key, fallbackLabel);
  return fallbackLabel;
}

async function enrichReports(rows = []) {
  const tasks = rows.map(async (row) => {
    const metadata = row.metadata && typeof row.metadata === 'object' ? row.metadata : {};
    const entityType = String(row.entityType || '').toLowerCase();
    const entityLabel = await resolveEntityLabel(
      entityType,
      row.entityId,
      firstNonEmpty(metadata.entityLabel, metadata.entityTitle, metadata.productTitle, metadata.profileUsername)
    );

    const reporterLabel = await resolveUserLabel(
      row.reporterId,
      firstNonEmpty(row.reporterName, row.reporterEmail)
    );

    const ownerLabel = row.entityOwnerId
      ? await resolveUserLabel(row.entityOwnerId, firstNonEmpty(metadata.entityOwnerName))
      : '';

    return {
      ...row,
      entityLabel,
      reporterLabel,
      entityOwnerLabel: ownerLabel,
    };
  });

  return Promise.all(tasks);
}

// ─── Activity Log ───────────────────────────────────────────────
/**
 * Log an admin action for audit trail
 */
export const logAdminAction = async (adminUser, action, details = {}, description = '') => {
  if (!adminUser) return;

  try {
    await addDoc(collection(db, COLLECTIONS.ACTIVITY_LOG), {
      adminId: adminUser.uid,
      adminEmail: adminUser.email,
      adminName: adminUser.displayName || adminUser.email?.split('@')[0] || 'Admin',
      action,
      description: toActivityDescription(action, details, description),
      details,
      createdAt: serverTimestamp(),
      // Keep timestamp for backward compat with existing log entries
      timestamp: serverTimestamp(),
    });
  } catch (err) {
    // Silent fail — audit logging should never block admin actions
  errorDev("Operation failed:", err);
  }
};

// ─── User Management ────────────────────────────────────────────

/**
 * Fetch paginated user list
 * @param {number} pageSize - Users per page
 * @param {DocumentSnapshot|null} lastDoc - Last document for pagination
 * @returns {Promise<{users: Array, lastDoc: DocumentSnapshot, hasMore: boolean}>}
 */
export const fetchUsers = async (pageSize = 25, lastDoc = null, filters = {}) => {
  const constraints = [
    orderBy(documentId(), 'asc'),
    limit(Math.max(pageSize * 4, pageSize + 1)),
  ];

  if (lastDoc) {
    constraints.splice(1, 0, startAfter(lastDoc));
  }

  const q = query(collection(db, COLLECTIONS.USERS), ...constraints);
  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  const filtered = rows.filter((user) => {
    if (filters.onlyActive === true && user.isActive === false) return false;
    if (filters.onlyBanned === true && user.banned !== true) return false;
    if (filters.role && user.role !== filters.role) return false;

    if (filters.profileState === 'complete' && user.hasCompletedProfile !== true) return false;
    if (filters.profileState === 'incomplete' && user.hasCompletedProfile === true) return false;

    return true;
  });

  filtered.sort((a, b) => {
    const aTime = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : new Date(a.createdAt || 0).getTime() || 0;
    const bTime = b.createdAt?.seconds ? b.createdAt.seconds * 1000 : new Date(b.createdAt || 0).getTime() || 0;
    return bTime - aTime;
  });

  const users = filtered.slice(0, pageSize);
  const hasMore = snap.docs.length > pageSize;
  const newLastDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;

  return { users, lastDoc: newLastDoc, hasMore };
};

/**
 * Search users by email or display name
 */
export const searchUsers = async (searchTerm, maxResults = 20) => {
  if (!searchTerm || searchTerm.length < 2) return [];

  const term = searchTerm.toLowerCase();
  
  // Search by email (exact prefix match)
  const emailQuery = query(
    collection(db, COLLECTIONS.USERS),
    where('email', '>=', term),
    where('email', '<=', term + '\uf8ff'),
    limit(maxResults)
  );

  const snap = await getDocs(emailQuery);
  const results = snap.docs.map(d => ({ id: d.id, ...d.data() }));

  // If few results, also search by displayName
  if (results.length < maxResults) {
    try {
      const nameQuery = query(
        collection(db, COLLECTIONS.USERS),
        where('displayName', '>=', term),
        where('displayName', '<=', term + '\uf8ff'),
        limit(maxResults - results.length)
      );
      const nameSnap = await getDocs(nameQuery);
      const nameResults = nameSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(u => !results.find(r => r.id === u.id));
      results.push(...nameResults);
    } catch (err) {
      // displayName field may not be indexed, skip
    errorDev("Operation failed:", err);
    }
  }

  return results;
};

/**
 * Update a user's role
 */
export const updateUserRole = async (adminUser, userId, newRole) => {
  if (!userId || !newRole) throw new Error('User ID and role are required');

  const userSnap = await getDoc(doc(db, COLLECTIONS.USERS, userId));
  const userData = userSnap.exists() ? userSnap.data() : {};
  const userLabel = firstNonEmpty(userData.displayName, userData.name, userData.username, userData.email, userId);

  await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
    role: newRole,
    roleUpdatedAt: serverTimestamp(),
    roleUpdatedBy: adminUser?.uid || null,
  });

  await logAdminAction(
    adminUser,
    'UPDATE_USER_ROLE',
    {
      userId,
      userName: userLabel,
      userEmail: userData.email || '',
      newRole,
    },
    `updated ${userLabel} role to ${newRole}`
  );
};

/**
 * Ban/unban a user
 */
export const toggleUserBan = async (adminUser, userId, banned, reason = '') => {
  if (!userId) throw new Error('User ID is required');

  const userSnap = await getDoc(doc(db, COLLECTIONS.USERS, userId));
  const userData = userSnap.exists() ? userSnap.data() : {};
  const userLabel = firstNonEmpty(userData.displayName, userData.name, userData.username, userData.email, userId);

  await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
    banned: !!banned,
    banReason: banned ? reason : null,
    bannedAt: banned ? serverTimestamp() : null,
    bannedBy: banned ? (adminUser?.uid || null) : null,
  });

  await logAdminAction(
    adminUser,
    banned ? 'BAN_USER' : 'UNBAN_USER',
    {
      userId,
      userName: userLabel,
      userEmail: userData.email || '',
      reason,
    },
    banned ? `banned ${userLabel}` : `unbanned ${userLabel}`
  );
};

/**
 * Get a single user document
 */
export const getUserById = async (userId) => {
  const snap = await getDoc(doc(db, COLLECTIONS.USERS, userId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

// ─── Admin Notifications ────────────────────────────────────────

/**
 * Send a notification (announcement, targeted, or role-based)
 * @param {Object} options
 * @param {string} options.title - Notification title
 * @param {string} options.message - Notification content
 * @param {string} options.type - 'announcement'|'targeted'|'popup'|'role_message'
 * @param {string[]} [options.targetUserIds] - For targeted notifications
 * @param {string} [options.targetRole] - For role-based messages ('user','admin','all')
 * @param {string} [options.priority] - 'low'|'normal'|'high'|'urgent'
 * @param {boolean} [options.requiresFeedback] - Whether to show a feedback form
 * @param {string} [options.feedbackPrompt] - Prompt for feedback form
 * @param {Date} [options.expiresAt] - When notification expires
 */
export const sendAdminNotification = async (adminUser, {
  title,
  message,
  type = 'announcement',
  targetUserIds = [],
  targetRole = 'all',
  priority = 'normal',
  requiresFeedback = false,
  feedbackPrompt = '',
  expiresAt = null,
  senderAlias = 'Orrith Team',
  theme = 'green',
  iconName = '',
  imageUrl = '',
}) => {
  if (!adminUser || !title || !message) throw new Error('Not authenticated');

  const notifData = {
    title,
    message,
    type,
    targetRole,
    priority,
    requiresFeedback,
    feedbackPrompt: requiresFeedback ? feedbackPrompt : '',
    senderAlias,
    theme,
    iconName,
    imageUrl,
    sentBy: adminUser.uid,
    sentByEmail: adminUser.email,
    createdAt: serverTimestamp(),
    expiresAt: expiresAt || null,
    isActive: true,
    readBy: [],
    dismissedBy: [],
    stats: { sent: 0, read: 0, dismissed: 0, feedbackCount: 0 },
  };

  // For targeted notifications, store target user IDs
  if (type === 'targeted' && targetUserIds.length > 0) {
    notifData.targetUserIds = targetUserIds;
  }

  // Store admin notification in its own collection
  const docRef = await addDoc(collection(db, COLLECTIONS.ADMIN_NOTIFICATIONS), notifData);

  // Also create individual notifications for each target user
  if (type === 'targeted' && targetUserIds.length > 0) {
    const batch = writeBatch(db);
    let batchCount = 0;

    for (const userId of targetUserIds) {
      const ref = doc(collection(db, COLLECTIONS.NOTIFICATIONS));
      batch.set(ref, {
        userId,
        toUserId: userId,
        fromUserId: null,
        type: 'ADMIN_NOTIFICATION',
        content: message,
        title,
        body: message,
        payload: {
          adminNotificationId: docRef.id,
          type,
          priority,
          requiresFeedback,
          feedbackPrompt: requiresFeedback ? feedbackPrompt : '',
          senderAlias,
          contextType: 'admin_notification',
        },
        isRead: false,
        createdAt: serverTimestamp(),
      });

      batchCount++;
      // Firestore batch limit is 500
      if (batchCount >= 499) break;
    }

    if (batchCount > 0) {
      await batch.commit();
      await updateDoc(doc(db, COLLECTIONS.ADMIN_NOTIFICATIONS, docRef.id), {
        'stats.sent': batchCount,
      });
    }
  }

  await logAdminAction(adminUser, 'SEND_NOTIFICATION', {
    notificationId: docRef.id,
    type,
    targetCount: targetUserIds.length,
    targetRole,
    title,
  }, `sent ${type} notification: "${title}"`);

  return { id: docRef.id };
};

/**
 * Send a role-based message (admin posts as a role persona)
 */
export const sendRoleMessage = async (adminUser, {
  title,
  message,
  senderAlias,
  targetRole = 'all',
  priority = 'normal',
  theme = 'green',
  iconName = '',
  imageUrl = '',
}) => {
  return sendAdminNotification(adminUser, {
    title,
    message,
    type: 'role_message',
    senderAlias,
    targetRole,
    priority,
    theme,
    iconName,
    imageUrl,
  });
};

/**
 * Listen to admin notifications in real-time
 */
export const listenAdminNotifications = (callback) => {
  const q = query(
    collection(db, COLLECTIONS.ADMIN_NOTIFICATIONS),
    orderBy('createdAt', 'desc'),
    limit(50)
  );

  return onSnapshot(
    q,
    (snap) => {
      const notifs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(notifs);
    },
    () => callback([])
  );
};

/**
 * Listen to active announcements for a user (non-admin view)
 * Requires explicit userId and callback:
 *   listenActiveAnnouncements(userId, callback)
 */
export const listenActiveAnnouncements = (...args) => {
  let userId, userRole, callback;

  if (args.length >= 3) {
    [userId, userRole, callback] = args;
  } else if (args.length === 2 && typeof args[1] === 'function') {
    [userId, callback] = args;
    userRole = null;
  } else {
    return () => {};
  }

  if (!userId || typeof callback !== 'function') return () => {};

  const buildVisibleNotifications = (docsOrRows) => {
    const now = new Date();
    return docsOrRows
      .map((row) => ({ id: row.id, ...row.data }))
      .filter((n) => {
        if (n.isActive === false) return false;
        if (n.expiresAt && n.expiresAt.toDate && n.expiresAt.toDate() < now) return false;
        if (n.dismissedBy?.includes(userId)) return false;
        if (n.targetRole && n.targetRole !== 'all' && n.targetRole !== userRole) return false;
        if (n.type === 'targeted' && n.targetUserIds && !n.targetUserIds.includes(userId)) return false;
        return true;
      });
  };

  // If userRole not provided, fetch it from Firestore
  const init = async () => {
    if (!userRole) {
      try {
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));
        userRole = userDoc.exists() ? (userDoc.data().role || 'user') : 'user';
      } catch (err) {
        // Best-effort role fetch — default to 'user' if it fails (e.g. rules deny,
        // network issue). Don't log to avoid noise for non-admin users who can't
        // read admin-specific data.
        userRole = 'user';
      }
    }
    return startListener();
  };

  let unsubFn = null;
  let pollTimer = null;

  const fetchFallback = async () => {
    try {
      const snap = await getDocs(
        query(
          collection(db, COLLECTIONS.ADMIN_NOTIFICATIONS),
          orderBy('createdAt', 'desc'),
          limit(40)
        )
      );
      const rows = snap.docs.map((d) => ({ id: d.id, data: d.data() }));
      callback(buildVisibleNotifications(rows));
    } catch (err) {
      // Keep existing UI state on fallback failure.
    errorDev("Operation failed:", err);
    }
  };

  const startListener = () => {
    const q = query(
      collection(db, COLLECTIONS.ADMIN_NOTIFICATIONS),
      orderBy('createdAt', 'desc'),
      limit(40)
    );

    // Prime the UI quickly even before first snapshot callback.
    fetchFallback();

    unsubFn = onSnapshot(
      q,
      (snap) => {
        if (pollTimer) {
          clearInterval(pollTimer);
          pollTimer = null;
        }
        const rows = snap.docs.map((d) => ({ id: d.id, data: d.data() }));
        callback(buildVisibleNotifications(rows));
      },
      () => {
        // Fall back to polling instead of blanking the UI.
        fetchFallback();
        if (!pollTimer) {
          pollTimer = setInterval(fetchFallback, 30000);
        }
      }
    );
    return unsubFn;
  };

  // Start async init, return cleanup function
  const initPromise = init();
  return () => {
    initPromise.then(unsub => unsub?.());
    unsubFn?.();
    if (pollTimer) clearInterval(pollTimer);
  };
};

/**
 * Mark an admin notification as read by user
 * @param {string} notificationId
 * @param {string} userId - Required: the user who read the notification
 */
export const markAdminNotificationRead = async (notificationId, userId) => {
  if (!notificationId || !userId) return;
  try {
    const ref = doc(db, COLLECTIONS.ADMIN_NOTIFICATIONS, notificationId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data();
      const readBy = data.readBy || [];
      if (!readBy.includes(userId)) {
        readBy.push(userId);
        await updateDoc(ref, {
          readBy,
          'stats.read': readBy.length,
        });
      }
    }
  } catch (err) {
    // Silent fail
    errorDev("Operation failed:", err);
  }
};

/**
 * Dismiss an admin notification for a user
 * @param {string} notificationId
 * @param {string} userId - Required: the user dismissing the notification
 */
export const dismissAdminNotification = async (notificationId, userId) => {
  if (!notificationId || !userId) return;
  try {
    const ref = doc(db, COLLECTIONS.ADMIN_NOTIFICATIONS, notificationId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data();
      const dismissedBy = data.dismissedBy || [];
      if (!dismissedBy.includes(userId)) {
        dismissedBy.push(userId);
        await updateDoc(ref, {
          dismissedBy,
          'stats.dismissed': dismissedBy.length,
        });
      }
    }
  } catch (err) {
    // Silent fail
    errorDev("Operation failed:", err);
  }
};

/**
 * Deactivate an admin notification
 */
export const deactivateAdminNotification = async (adminUser, notificationId) => {
  await updateDoc(doc(db, COLLECTIONS.ADMIN_NOTIFICATIONS, notificationId), {
    isActive: false,
    deactivatedAt: serverTimestamp(),
    deactivatedBy: adminUser?.uid || null,
  });
  await logAdminAction(adminUser, 'DEACTIVATE_NOTIFICATION', { notificationId }, 'deactivated a notification');
};

/**
 * Delete an admin notification/message permanently.
 */
export const deleteAdminNotification = async (adminUser, notificationId) => {
  await deleteDoc(doc(db, COLLECTIONS.ADMIN_NOTIFICATIONS, notificationId));
  await logAdminAction(adminUser, 'DELETE_NOTIFICATION', { notificationId }, 'deleted a notification');
};

/**
 * Listen to feedback for admin view
 */
export const listenFeedback = (callback, statusFilter = null) => {
  let q;
  if (statusFilter) {
    q = query(
      collection(db, COLLECTIONS.ADMIN_FEEDBACK),
      where('status', '==', statusFilter),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
  } else {
    q = query(
      collection(db, COLLECTIONS.ADMIN_FEEDBACK),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
  }

  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(items);
    },
    () => callback([])
  );
};

/**
 * Update feedback status
 */
export const updateFeedbackStatus = async (adminUser, feedbackId, status, adminNote = '') => {
  const feedbackRef = doc(db, COLLECTIONS.ADMIN_FEEDBACK, feedbackId);
  const rewardResult = await runTransaction(db, async (tx) => {
    const feedbackSnap = await tx.get(feedbackRef);
    if (!feedbackSnap.exists()) {
      throw new Error('Feedback not found');
    }

    const feedback = feedbackSnap.data() || {};
    const patch = {
      status,
      adminNote,
      reviewedAt: serverTimestamp(),
      reviewedBy: adminUser?.uid || null,
    };

    let awardedUserId = null;
    let awardedPoints = 0;

    if (status === 'helpful' && feedback?.userId && !feedback?.pointsAwarded) {
      patch.pointsAwarded = true;
      patch.pointsAwardedAt = serverTimestamp();
      patch.pointsAwardedValue = 10;
      awardedUserId = String(feedback.userId);
      awardedPoints = 10;

      awardImpactPointsInTransaction(tx, {
        userId: awardedUserId,
        action: IMPACT_ACTIONS.HELPFUL_FEEDBACK,
        metadata: {
          feedbackId,
          category: feedback?.category || 'general',
        },
      });
    }

    tx.update(feedbackRef, patch);

    return { awardedUserId, awardedPoints };
  });

  if (rewardResult.awardedUserId && rewardResult.awardedPoints > 0) {
    try {
      await notifyPointsEarned({
        userId: rewardResult.awardedUserId,
        points: rewardResult.awardedPoints,
        reason: 'helpful feedback',
      });
    } catch (err) {
      // Keep status update even if points notification fails.
    errorDev("Operation failed:", err);
    }
  }

    await logAdminAction(adminUser, 'UPDATE_FEEDBACK_STATUS', { feedbackId, status }, `updated feedback to ${status}`);
};

// ─── Support Tickets (Admin View) ───────────────────────────────

/**
 * Listen to all support tickets for admin management
 */
export const listenAllTickets = (callback, statusFilter = null) => {
  let q;
  if (statusFilter && statusFilter !== 'all') {
    q = query(
      collection(db, COLLECTIONS.SUPPORT_TICKETS),
      where('status', '==', statusFilter),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
  } else {
    q = query(
      collection(db, COLLECTIONS.SUPPORT_TICKETS),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
  }

  return onSnapshot(
    q,
    (snap) => {
      const tickets = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(tickets);
    },
    () => callback([])
  );
};

/**
 * Update support ticket status
 */
export const updateTicketStatus = async (adminUser, ticketId, status, adminResponse = '') => {
  const updateData = {
    status,
    updatedAt: serverTimestamp(),
    assignedTo: adminUser?.uid || null,
  };

  if (adminResponse) {
    updateData.adminResponse = adminResponse;
    updateData.respondedAt = serverTimestamp();
  }

  await updateDoc(doc(db, COLLECTIONS.SUPPORT_TICKETS, ticketId), updateData);
  await logAdminAction(adminUser, 'UPDATE_TICKET', { ticketId, status }, `updated ticket status to ${status}`);
};

/**
 * Add admin reply to a ticket
 */
export const replyToTicket = async (adminUser, ticketId, message) => {
  if (!adminUser || !message) throw new Error('Admin user and message are required');

  const ticketRef = doc(db, COLLECTIONS.SUPPORT_TICKETS, ticketId);
  const ticketSnap = await getDoc(ticketRef);
  if (!ticketSnap.exists()) throw new Error('Ticket not found');

  const ticketData = ticketSnap.data();
  const replies = ticketData.replies || [];
  
  replies.push({
    message,
    sentBy: adminUser.uid,
    sentByEmail: adminUser.email,
    isAdmin: true,
    createdAt: new Date().toISOString(),
  });

  await updateDoc(ticketRef, {
    replies,
    status: 'in_progress',
    updatedAt: serverTimestamp(),
    assignedTo: adminUser.uid,
  });

  // Notify the ticket owner
  if (ticketData.userId) {
    try {
      await addDoc(collection(db, COLLECTIONS.NOTIFICATIONS), {
        userId: ticketData.userId,
        toUserId: ticketData.userId,
        fromUserId: null,
        type: 'system',
        content: `Your support ticket "${ticketData.subject || 'Support Request'}" received a reply.`,
        title: 'Support Reply',
        body: message.substring(0, 100),
        payload: { ticketId, contextType: 'support_reply' },
        isRead: false,
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      // Silent fail
    errorDev("Operation failed:", err);
    }
  }

  await logAdminAction(adminUser, 'REPLY_TICKET', { ticketId }, 'replied to a support ticket');
};

// ─── Content Moderation ─────────────────────────────────────────

/**
 * Fetch reported/flagged products
 */
export const fetchFlaggedProducts = async (maxResults = 30) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.PRODUCTS),
      where('flagged', '==', true),
      orderBy('flaggedAt', 'desc'),
      limit(maxResults)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    // Fallback without composite index: fetch flagged then sort client-side.
    errorDev("Operation failed:", err);
    const fallbackQ = query(
      collection(db, COLLECTIONS.PRODUCTS),
      where('flagged', '==', true),
      limit(maxResults * 3)
    );
    const snap = await getDocs(fallbackQ);
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => {
        const aTs = a.flaggedAt?.seconds || a.createdAt?.seconds || 0;
        const bTs = b.flaggedAt?.seconds || b.createdAt?.seconds || 0;
        return bTs - aTs;
      })
      .slice(0, maxResults);
  }
};

/**
 * Moderate a product (approve/remove/flag)
 */
export const moderateProduct = async (adminUser, productId, action, reason = '') => {
  const productRef = doc(db, COLLECTIONS.PRODUCTS, productId);
  const productSnap = await getDoc(productRef);
  const productData = productSnap.exists() ? productSnap.data() : {};
  const productLabel = firstNonEmpty(productData.title, productData.name, productData.productName, productId);

  const updates = {
    moderatedAt: serverTimestamp(),
    moderatedBy: adminUser?.uid || null,
    moderationReason: reason,
  };

  switch (action) {
    case 'approve':
      updates.flagged = false;
      updates.isHidden = false;
      break;
    case 'hide':
      updates.isHidden = true;
      updates.hiddenReason = reason;
      break;
    case 'remove':
      updates.isHidden = true;
      updates.removed = true;
      updates.removedReason = reason;
      break;
    default:
      break;
  }

  await updateDoc(productRef, updates);
  await logAdminAction(
    adminUser,
    'MODERATE_PRODUCT',
    {
      productId,
      action,
      reason,
      entityType: 'product',
      entityId: productId,
      entityLabel: productLabel,
      productTitle: productLabel,
    },
    action === 'remove' ? `removed ${productLabel}` : action === 'hide' ? `hid ${productLabel}` : `approved ${productLabel}`
  );
};

/**
 * Listen to entity reports for admin review.
 */
export const listenReports = (callback, filters = {}) => {
  if (typeof callback !== 'function') return () => {};

  const constraints = [];

  if (filters?.status && filters.status !== 'all') {
    constraints.push(where('status', '==', filters.status));
  }

  if (filters?.entityType && filters.entityType !== 'all') {
    constraints.push(where('entityType', '==', filters.entityType));
  }

  constraints.push(orderBy('createdAt', 'desc'));
  constraints.push(limit(filters?.limit || 100));

  const q = query(collection(db, COLLECTIONS.REPORTS), ...constraints);

  return onSnapshot(
    q,
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      enrichReports(rows)
        .then((enriched) => callback(enriched))
        .catch(() => callback(rows));
    },
    () => callback([])
  );
};

/**
 * Update report status and optional admin decision notes.
 */
export const updateReportStatus = async (adminUser, reportId, status, adminNote = '') => {
  if (!reportId || !status) throw new Error('reportId and status are required');
  if (!adminUser) throw new Error('Admin user is required');

  const reportRef = doc(db, COLLECTIONS.REPORTS, reportId);
  const reportSnap = await getDoc(reportRef);
  const reportData = reportSnap.exists() ? reportSnap.data() : {};
  const previousStatus = reportData?.status || 'open';

  const entityType = String(reportData?.entityType || '').toLowerCase();
  const entityLabel = await resolveEntityLabel(
    entityType,
    reportData?.entityId,
    firstNonEmpty(
      reportData?.metadata?.entityLabel,
      reportData?.metadata?.entityTitle,
      reportData?.metadata?.productTitle,
      reportData?.metadata?.profileUsername
    )
  );

  await updateDoc(reportRef, {
    status,
    adminNote,
     reviewedBy: adminUser.uid,
     reviewedByEmail: adminUser.email || '',
    reviewedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await logAdminAction(
    adminUser,
    'UPDATE_REPORT_STATUS',
    {
      reportId,
      status,
      previousStatus,
      entityType,
      entityId: reportData?.entityId || null,
      entityLabel,
    },
    `moved ${entityLabel} report from ${statusLabel(previousStatus)} to ${statusLabel(status)}`
  );
};

export const deleteReport = async (adminUser, reportId) => {
  if (!reportId) throw new Error('reportId is required');

  await ensureSuperAdmin(adminUser);

  const reportRef = doc(db, COLLECTIONS.REPORTS, reportId);
  const reportSnap = await getDoc(reportRef);
  const data = reportSnap.exists() ? reportSnap.data() : {};
  const entityType = String(data?.entityType || '').toLowerCase();
  const entityLabel = await resolveEntityLabel(
    entityType,
    data?.entityId,
    firstNonEmpty(
      data?.metadata?.entityLabel,
      data?.metadata?.entityTitle,
      data?.metadata?.productTitle,
      data?.metadata?.profileUsername
    )
  );

  await deleteDoc(reportRef);

  await logAdminAction(
    adminUser,
    'DELETE_REPORT',
    {
      reportId,
      entityType,
      entityId: data?.entityId || null,
      entityLabel,
    },
    `deleted report for ${entityLabel}`
  );
};

export const deleteReportsBulk = async (adminUser, reportIds = []) => {
  const ids = Array.from(new Set((reportIds || []).filter(Boolean)));
  if (!ids.length) return;

  await ensureSuperAdmin(adminUser);

  const chunkSize = 400;
  for (let start = 0; start < ids.length; start += chunkSize) {
    const chunk = ids.slice(start, start + chunkSize);
    const batch = writeBatch(db);
    chunk.forEach((id) => {
      batch.delete(doc(db, COLLECTIONS.REPORTS, id));
    });
    await batch.commit();
  }

  await logAdminAction(
    adminUser,
    'BULK_DELETE_REPORTS',
    { count: ids.length },
    `deleted ${ids.length} reports`
  );
};

// ─── Platform Analytics (Spark-compatible) ──────────────────────

const PLATFORM_STATS_TTL_MS = 5 * 60 * 1000;
const QUOTA_ERROR_CODES = new Set([
  'resource-exhausted',
  'quota-exceeded',
]);

let platformStatsCache = null;
let platformStatsCacheAt = 0;
let platformStatsInflight = null;

const isQuotaError = (err) => {
  const code = String(err?.code || err?.details?.firestoreCode || err?.message || '').toLowerCase();
  if (!code) return false;
  return err?.status === 429
    || /quota|rate limit/i.test(code)
    || Array.from(QUOTA_ERROR_CODES).some((marker) => code.includes(marker));
};

const safeCount = (name) => withAdminRetry(async () => {
  try {
    const snap = await getCountFromServer(collection(db, name));
    return { value: snap.data().count, ok: true };
  } catch (err) {
    errorDev(`[admin stats] Could not count ${name}:`, err);
    return { value: 0, ok: false };
  }
});

const safeFilteredCount = (name, field, values) => withAdminRetry(async () => {
  try {
    const snap = await getCountFromServer(query(
      collection(db, name),
      where(field, 'in', values),
    ));
    return { value: snap.data().count, ok: true };
  } catch (err) {
    errorDev(`[admin stats] Could not count ${name} by ${field}:`, err);
    return { value: 0, ok: false };
  }
});

/**
 * Get platform stats — counts from Firestore (no Cloud Functions needed).
 * Results are cached for 5 minutes and concurrent callers share one request.
 * Quota/resource errors fall back to safe zero values without throwing.
 */
export const getPlatformStats = async () => {
  const now = Date.now();
  if (platformStatsCache && (now - platformStatsCacheAt) < PLATFORM_STATS_TTL_MS) {
    return platformStatsCache;
  }

  if (platformStatsInflight) {
    return platformStatsInflight;
  }

  const run = async () => {
    const counts = {};
    const previousStats = platformStatsCache;

    const [users, products, tickets, ratings, offers, reports] = await Promise.all([
      safeCount(COLLECTIONS.USERS),
      safeCount(COLLECTIONS.PRODUCTS),
      safeCount(COLLECTIONS.SUPPORT_TICKETS),
      safeCount(COLLECTIONS.RATINGS),
      safeCount(COLLECTIONS.OFFERS),
      safeCount(COLLECTIONS.REPORTS),
    ]);

    counts.totalUsers = users.ok ? users.value : (previousStats?.totalUsers ?? 0);
    counts.totalProducts = products.ok ? products.value : (previousStats?.totalProducts ?? 0);
    counts.totalTickets = tickets.ok ? tickets.value : (previousStats?.totalTickets ?? 0);
    counts.totalRatings = ratings.ok ? ratings.value : (previousStats?.totalRatings ?? 0);
    counts.totalOffers = offers.ok ? offers.value : (previousStats?.totalOffers ?? 0);
    counts.totalReports = reports.ok ? reports.value : (previousStats?.totalReports ?? 0);

    const recentUsers = await withAdminRetry(async () => {
      try {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const recentUsersSnap = await getCountFromServer(query(
          collection(db, COLLECTIONS.USERS),
          where('createdAt', '>=', sevenDaysAgo),
        ));
        return { value: recentUsersSnap.data().count, ok: true };
      } catch (err) {
          errorDev('[admin stats] Could not count recent users:', err);
          return { value: 0, ok: false };
      }
    });
    counts.newUsersThisWeek = recentUsers.ok
      ? recentUsers.value
      : (previousStats?.newUsersThisWeek ?? 0);

    const openTickets = await safeFilteredCount(
      COLLECTIONS.SUPPORT_TICKETS,
      'status',
      ['open', 'new', 'pending'],
    );
    counts.openTickets = openTickets.ok
      ? openTickets.value
      : (previousStats?.openTickets ?? 0);

    const openReports = await safeFilteredCount(
      COLLECTIONS.REPORTS,
      'status',
      ['open', 'under_review'],
    );
    counts.openReports = openReports.ok
      ? openReports.value
      : (previousStats?.openReports ?? 0);

    platformStatsCache = counts;
    platformStatsCacheAt = Date.now();
    return counts;
  };

  const failed = (err) => {
    const fallback = platformStatsCache || {
      totalUsers: 0,
      totalProducts: 0,
      totalTickets: 0,
      totalRatings: 0,
      totalOffers: 0,
      totalReports: 0,
      newUsersThisWeek: 0,
      openTickets: 0,
      openReports: 0,
    };
    errorDev("Operation failed:", err);
    return { ...fallback };
  };

  platformStatsInflight = run().catch(failed).finally(() => {
    platformStatsInflight = null;
  });

  return platformStatsInflight;
};

/**
 * Listen to admin activity log
 */
export const listenActivityLog = (callbackOrLimit, callbackOrMax = null) => {
  // Support both call signatures:
  //   listenActivityLog(callback, maxEntries)
  //   listenActivityLog(maxEntries, callback)  <-- legacy/wrong order, still works
  let callback, maxEntries;
  if (typeof callbackOrLimit === 'function') {
    callback = callbackOrLimit;
    maxEntries = typeof callbackOrMax === 'number' ? callbackOrMax : 30;
  } else if (typeof callbackOrMax === 'function') {
    callback = callbackOrMax;
    maxEntries = typeof callbackOrLimit === 'number' ? callbackOrLimit : 30;
  } else {
    // Fallback: no valid callback
    return () => {};
  }

  const q = query(
    collection(db, COLLECTIONS.ACTIVITY_LOG),
    orderBy('createdAt', 'desc'),
    limit(maxEntries)
  );

  return onSnapshot(
    q,
    (snap) => {
      const logs = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .map((entry) => decorateActivityEntry(entry));
      callback(logs);
    },
    () => callback([])
  );
};

export const deleteActivityLogEntry = async (adminUser, logId) => {
  if (!logId) throw new Error('logId is required');
  await deleteDoc(doc(db, COLLECTIONS.ACTIVITY_LOG, logId));
  await logAdminAction(adminUser, 'DELETE_LOG_ENTRY', { logId }, 'deleted a log entry');
};

export const deleteActivityLogEntries = async (adminUser, logIds = []) => {
  const ids = Array.from(new Set((logIds || []).filter(Boolean)));
  if (!ids.length) return;

  const chunkSize = 400;
  for (let start = 0; start < ids.length; start += chunkSize) {
    const chunk = ids.slice(start, start + chunkSize);
    const batch = writeBatch(db);
    chunk.forEach((id) => {
      batch.delete(doc(db, COLLECTIONS.ACTIVITY_LOG, id));
    });
    await batch.commit();
  }

  await logAdminAction(
    adminUser,
    'BULK_DELETE_LOG_ENTRIES',
    { count: ids.length },
    `deleted ${ids.length} log entries`
  );
};

/**
 * Get recent signups for growth chart
 */
export const getRecentSignups = async (days = 30) => {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);

  try {
    const q = query(
      collection(db, COLLECTIONS.USERS),
      where('createdAt', '>=', cutoff),
      orderBy('createdAt', 'asc'),
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    errorDev("Operation failed:", err);
    return [];
  }
};

// ─── Canonical Naming Aliases ──────────────────────────────────
// Prefer these names in new code for consistency across services.
export const listUsers = fetchUsers;
export const findUsers = searchUsers;
export const getUser = getUserById;
export const createAdminNotification = sendAdminNotification;
export const listAdminFeedback = listenFeedback;
export const listSupportTickets = listenAllTickets;
